import { Box, Tooltip, type TooltipProps } from '@mui/material';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { formatPatientId } from '../model';

type PatientIdTextProps = {
    id: string | null | undefined;
    /** Which way the full ID opens; a table row passes `right`, into its
     *  remaining inert cells. */
    tooltipPlacement?: TooltipProps['placement'];
};

/**
 * A patient's DICOM ID in the patient list's mono style.
 *
 * A long ID is middle-truncated so it cannot crowd out the name, and two IDs
 * that differ only in the elided middle then render alike — so the full value
 * stays reachable on hover, blurred with the rest of the identity while PHI is
 * hidden. Truncated IDs alone must never be what a patient is identified by.
 */
export default function PatientIdText({
    id,
    tooltipPlacement,
}: PatientIdTextProps) {
    const text = formatPatientId(id);
    const content = (
        <Box
            component="span"
            sx={{
                display: 'block',
                fontFamily: 'fontFamilyMonospace',
                fontSize: 'inherit',
                color: 'inherit',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            <SensitiveInfoTypography>{text}</SensitiveInfoTypography>
        </Box>
    );
    if (!id || text === id) {
        return content;
    }
    return (
        <Tooltip
            title={<SensitiveInfoTypography>{id}</SensitiveInfoTypography>}
            placement={tooltipPlacement}
        >
            {content}
        </Tooltip>
    );
}
